/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

// Component imports
import { Drawer, Grid, Layout, Menu, Tooltip } from 'antd';
import LabSwitcher from './LabSwitcher';
import BoxIcon from '../Icons/BoxIcon';
import { PlayCircleOutlined } from '@ant-design/icons';

// Misc imports
import { NAV_ANALYTICS, NAV_BOT, NAV_BOTS, NAV_CHAT_SESSIONS, NAV_CONTACTS, NAV_DASHBOARD, NAV_LIVECHAT, NAV_MEDIA, NAV_MISSED_QUESTIONS, NAV_MONITORING, NAV_ORGANIZATION_INTEGRATIONS, NAV_ORGANIZATION_KNOWLEDGE_BASES, NAV_ORGANIZATION_LOGS, NAV_ORGANIZATION_SETTINGS, NAV_ORGANIZATION_USERS, NAV_SCHEDULED_REPORTS, NAV_SURVEYS, SIDEBAR_MENU_ITEMS, NAV_ORGANIZATION_TESTING } from '../../constants/navigation';
import { ADDITIONAL_ROLES_ORG, DEFAULT_DATETIME_TIMEZONE_FORMAT, LABS, PERMISSIONS_ORG, PERMISSIONS_TENANT } from '../../constants/main';
import { IS_AUTOMATED_AGENT_TESTING_ENABLED, IS_CONTACTS_ENABLED, IS_LIVECHAT_CONVERSATIONS_ENABLED, IS_MEDIA_MANAGER_ENABLED, IS_SCHEDULED_REPORTS_ENABLED, IS_LIVECHAT_CONSOLE_ONLY } from '../../constants/env';
import { LIVECHAT_SCREEN_TABS } from '../../constants/liveChat';
import { toggleSidebarOpen } from '../../store/global/globalActions';
import { useFetchBotQuery } from '../../store/bots/botsApi';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import hasLiveChatAccess from '../../utils/misc/hasLiveChatAccess';
import useTheme from '../../utils/hooks/useTheme';
import is from '../../utils/is';

// Constant declarations
const { Sider } = Layout;
const { useBreakpoint } = Grid;

const DEFAULT_BOT_VIEW = SIDEBAR_MENU_ITEMS.BEHAVIOR.key;
const baseClassName = 'hbf-sider';

/**
 * Shows an icon with an optional tooltip for use in the sidebar menus.
 * @param title - The title text displayed in the tooltip
 * @param icon - The icon element to be rendered
 * @param hidden - Whether to hide the icon completely
 * @param showTooltip - Whether to show the tooltip on hover (typically only when the sidebar is collapsed)
 * @param isOrgIcon - Determines tooltip styling - true for organization menu icons, false or empty for lab menu icons
 * @returns The rendered icon with tooltip wrapper, or null if hidden
 */
const MenuIcon = ({
  title,
  icon,
  hidden,
  showTooltip = true,
  isOrgIcon
}) => (
  <Tooltip
    title={showTooltip ? title : ''}
    placement="right"
    overlayClassName={classNames('pointer-events-none', {
      [`${baseClassName}__org-menu__tooltip`]: isOrgIcon,
      [`${baseClassName}__lab-menu__tooltip`]: !isOrgIcon
    })}
  >
    {!hidden ? icon : null}
  </Tooltip>
);

const GlobalSider = () => {
  const { t } = useTranslation();
  const { tenantId, orgId } = useParams();
  const { paddingXs } = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const searchParams = new URLSearchParams(location.search);

  const { currentUser } = useSelector((state) => state.auth);
  const { organizationRoles, tenantRoles } = useSelector(() => currentUser);
  const userPermissions = useUserPermissions(orgId, tenantId);
  const isAdmin = userHasEditAccess(orgId, organizationRoles, tenantId, tenantRoles, false);

  const { data: bot } = useFetchBotQuery({ orgId, tenantId }, { skip: !tenantId });
  const { isSidebarOpen } = useSelector((state) => state.global);
  const { isFullScreenMode } = useSelector((state) => state.global);

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [defaultOpenKeys, setDefaultOpenKeys] = useState([]);

  const getInitialLab = () => {
    if (IS_LIVECHAT_CONSOLE_ONLY) {
      return LABS.LIVECHAT.key;
    }

    if (tenantId || location.pathname.includes('/bots')) {
      return LABS.BOT.key;
    } else if (location.pathname.includes('/livechat')) {
      return LABS.LIVECHAT.key;
    } else if (location.pathname.includes('/observatory')) {
      return LABS.OBSERVATORY.key;
    } else {
      return LABS.ORG.key;
    }
  };

  const [isLabSidebarVisible, setIsLabSidebarVisible] = useState(false);
  const [currentLab, setCurrentLab] = useState(getInitialLab);

  const isOrgSiderCollapsed = !isSidebarOpen || currentLab !== LABS.ORG.key;

  useEffect(() => {
    // If the view opens in mobile size, ensure the drawer is closed by default
    if (isMobileOrTabletSize) {
      toggleOpen(false);
    }
  }, [bot]);

  useEffect(() => {
    // If the current lab is not the organization, we need to show the lab sidebar
    if (currentLab !== LABS.ORG.key) {
      setIsLabSidebarVisible(true);
    } else {
      setIsLabSidebarVisible(false);
    }
  }, [currentLab]);

  // Initialize the state of the sidebar
  useEffect(() => {
    if (tenantId) {
      setCurrentLab(LABS.BOT.key);

      // Set default view and tab, if none is specified
      if (!searchParams.get('view')) {
        navigate({
          pathname: location.pathname,
          search: `view=${DEFAULT_BOT_VIEW}`
        }, { replace: true });

        // If we're in mobile screens, it means a drawer is open, so close it
        if (isMobileOrTabletSize) {
          toggleOpen(false);
        }
      }
    } else if (location.pathname.includes('/livechat')) {
      setCurrentLab(LABS.LIVECHAT.key);
    } else if (location.pathname.includes('/observatory')) {
      setCurrentLab(LABS.OBSERVATORY.key);
    } else {
      setCurrentLab(LABS.ORG.key);
    }
  }, [location, tenantId]);

  // Add effect to update selected keys based on current path
  useEffect(() => {
    const pathname = location.pathname;

    // Set lab menu selected keys based on query params
    if (tenantId) {
      // Handle bot views
      const view = searchParams.get('view');
      const tab = searchParams.get('tab');
      setSelectedKeys([`bots${view ? `-${view}` : ''}${tab ? `-${tab}` : ''}`]);
      setDefaultOpenKeys([`bots${view ? `-${view}` : ''}`]);
    } else if (pathname.includes('/livechat')) {
      // Handle livechat views
      const tab = searchParams.get('tab');
      setSelectedKeys([`livechat${tab ? `-${tab}` : ''}`]);
    } else if (pathname.includes('/observatory')) {
      // Handle observatory views
      const tab = searchParams.get('tab');
      const lastPathSegment = pathname.split('/').pop();
      setSelectedKeys([`observatory${lastPathSegment ? `-${lastPathSegment}` : ''}${tab ? `-${tab}` : ''}`]);
      setDefaultOpenKeys([`observatory${tab ? `-${tab}` : ''}`]);
    } else {
      // Handle org views
      if (pathname.includes('/knowledge-bases')) {
        setSelectedKeys(['org-knowledge-bases']);
      } else if (pathname.includes('/integrations')) {
        setSelectedKeys(['org-integrations']);
      } else if (pathname.includes('/media')) {
        setSelectedKeys(['org-media']);
      } else if (pathname.includes('/settings')) {
        const tab = searchParams.get('tab');
        setSelectedKeys([`org-settings${tab ? `-${tab}` : ''}`]);
        setDefaultOpenKeys(['org-settings']);
      } else if (pathname.includes('/users')) {
        const tab = searchParams.get('tab');
        setSelectedKeys([`org-settings-users${tab ? `-${tab}` : ''}`]);
        setDefaultOpenKeys(['org-settings']);
      } else if (pathname.includes('/logs')) {
        setSelectedKeys(['org-logs']);
      } else if (pathname.includes('/contacts')) {
        setSelectedKeys(['org-contacts']);
      } else if (pathname.includes('/bots')) {
        setSelectedKeys(['org-bots']);
      } else {
        setSelectedKeys(['org']);
      }
    }
  }, [location.pathname, location.search, tenantId, orgId]);

  const toggleOpen = (isOpen) => {
    dispatch(toggleSidebarOpen(isOpen));
  };

  const renderLabMenu = () => {
    switch (currentLab) {
      case LABS.BOT.key:
        return (
          <Fragment>
            <Menu
              theme="light"
              mode="inline"
              selectedKeys={selectedKeys.length > 0 ? selectedKeys : [searchParams.get('view') || DEFAULT_BOT_VIEW]}
              defaultOpenKeys={defaultOpenKeys.length > 0 ? defaultOpenKeys : []}
              className={classNames(`${baseClassName}__lab-menu scrollbar-custom ${baseClassName}__lab-menu--bot`, {
                'ant-menu-inline-collapsed': !isSidebarOpen
              })}
              items={[
                ...tenantId ? [
                  ...bot && bot.powerups && SIDEBAR_MENU_ITEMS.BEHAVIOR.relatedPowerups.some(powerup => bot.powerups.includes(powerup)) ? [{
                    key: `bots-${SIDEBAR_MENU_ITEMS.BEHAVIOR.key}`,
                    icon: <MenuIcon title={t(bot.systemSettings?.isAgent ? 'AI' : 'Behavior')} icon={SIDEBAR_MENU_ITEMS.BEHAVIOR.icon} showTooltip={!isSidebarOpen} />,
                    label: bot.systemSettings?.isAgent ? t('AI') : t('Behavior'),
                    onTitleClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'behavior' }).toString()}`),
                    children: [
                      {
                        key: 'bots-behavior-flows',
                        icon: <MenuIcon title={t('Flows')} icon={<BoxIcon name="vector" />} hidden={isSidebarOpen} />,
                        label: t('Flows'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'behavior', tab: 'flows' }).toString()}`)
                      },
                      {
                        key: 'bots-behavior-static-variables',
                        icon: <MenuIcon title={t('Variables')} icon={<BoxIcon name="code-alt" />} hidden={isSidebarOpen} />,
                        label: t('Variables'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'behavior', tab: 'static-variables' }).toString()}`)
                      },
                      {
                        key: 'bots-behavior-fallbacks',
                        icon: <MenuIcon title={t('Default Flow')} icon={<BoxIcon name="comment-dots" />} hidden={isSidebarOpen} />,
                        label: t('Default Flow'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'behavior', tab: 'fallbacks' }).toString()}`)
                      },
                      ...!bot.systemSettings?.isAgent ? [{
                        key: 'bots-behavior-automated-answers',
                        icon: <MenuIcon title={t('Automated Answers')} icon={<BoxIcon name="conversation" />} hidden={isSidebarOpen} />,
                        label: t('Automated Answers'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'behavior', tab: 'automated-answers' }).toString()}`)
                      }] : [],
                      ...isAdmin ? [{
                        key: 'bots-behavior-translations',
                        icon: <MenuIcon title={t('Translations')} icon={<BoxIcon name="globe" />} hidden={isSidebarOpen} />,
                        label: t('Translations'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'behavior', tab: 'translations' }).toString()}`)
                      }] : []
                    ]
                  }] : [],
                  ...bot && isAdmin ? [{
                    key: `bots-${SIDEBAR_MENU_ITEMS.PLUGINS.key}`,
                    icon: <MenuIcon title={t('Plugins')} icon={SIDEBAR_MENU_ITEMS.PLUGINS.icon} showTooltip={!isSidebarOpen} />,
                    label: t('Plugins'),
                    onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'plugins' }).toString()}`)

                  }] : [],
                  ...bot ? [{
                    key: `bots-${SIDEBAR_MENU_ITEMS.DEPLOYMENTS.key}`,
                    icon: <MenuIcon title={t('Deployments')} icon={SIDEBAR_MENU_ITEMS.DEPLOYMENTS.icon} showTooltip={!isSidebarOpen} />,
                    label: t('Deployments'),
                    onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'deployments' }).toString()}`)
                  }] : [],
                  ...bot && !bot.systemSettings?.isAgent && bot.powerups && SIDEBAR_MENU_ITEMS.ARTIFICIAL_INTELLIGENCE.relatedPowerups.some(powerup => bot.powerups.includes(powerup)) ? [{
                    key: `bots-${SIDEBAR_MENU_ITEMS.ARTIFICIAL_INTELLIGENCE.key}`,
                    icon: <MenuIcon title={t('AI')} icon={SIDEBAR_MENU_ITEMS.ARTIFICIAL_INTELLIGENCE.icon} showTooltip={!isSidebarOpen} />,
                    label: t('AI'),
                    onTitleClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'artificial-intelligence' }).toString()}`),
                    children: [
                      {
                        key: 'bots-artificial-intelligence-nlp-configuration',
                        icon: <MenuIcon title={t('NLP Configuration')} icon={<BoxIcon name="cog" />} hidden={isSidebarOpen} />,
                        label: t('NLP Configuration'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'artificial-intelligence', tab: 'nlp-configuration' }).toString()}`)
                      },
                      {
                        key: 'bots-artificial-intelligence-nlp-systems',
                        icon: <MenuIcon title={t('NLP Systems')} icon={<BoxIcon name="customize" />} hidden={isSidebarOpen} />,
                        label: t('NLP Systems'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'artificial-intelligence', tab: 'nlp-systems' }).toString()}`)
                      },
                      {
                        key: 'bots-artificial-intelligence-nlp-flows',
                        icon: <MenuIcon title={t('NLP Flows')} icon={<BoxIcon name="vector" />} hidden={isSidebarOpen} />,
                        label: t('NLP Flows'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'artificial-intelligence', tab: 'nlp-flows' }).toString()}`)
                      },
                      {
                        key: 'bots-artificial-intelligence-test-nlps',
                        icon: <MenuIcon title={t('NLP Testing')} icon={<BoxIcon name="badge-check" />} hidden={isSidebarOpen} />,
                        label: t('NLP Testing'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'artificial-intelligence', tab: 'test-nlps' }).toString()}`)
                      },
                      {
                        key: 'bots-artificial-intelligence-nlp-keywords',
                        icon: <MenuIcon title={t('Priority Keywords')} icon={<BoxIcon name="spreadsheet" />} hidden={isSidebarOpen} />,
                        label: t('Priority Keywords'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'artificial-intelligence', tab: 'nlp-keywords' }).toString()}`)
                      }
                    ]
                  }] : [],
                  ...bot && bot.powerups && SIDEBAR_MENU_ITEMS.BROADCASTS.relatedPowerups.some(powerup => bot.powerups.includes(powerup)) ? [{
                    key: `bots-${SIDEBAR_MENU_ITEMS.BROADCASTS.key}`,
                    icon: <MenuIcon title={t('Engagement')} icon={SIDEBAR_MENU_ITEMS.BROADCASTS.icon} showTooltip={!isSidebarOpen} />,
                    label: t('Engagement'),
                    onTitleClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'broadcasts' }).toString()}`),
                    children: [
                      {
                        key: 'bots-broadcasts-broadcasts',
                        icon: <MenuIcon title={t('Broadcasts')} icon={<BoxIcon name="conversation" />} hidden={isSidebarOpen} />,
                        label: t('Broadcasts'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'broadcasts', tab: 'broadcasts' }).toString()}`)
                      },
                      {
                        key: 'bots-broadcasts-audience',
                        icon: <MenuIcon title={t('Audiences')} icon={<BoxIcon name="group" />} hidden={isSidebarOpen} />,
                        label: t('Audiences'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'broadcasts', tab: 'audience' }).toString()}`)
                      }
                    ]
                  }] : [],
                  ...bot && bot.powerups && SIDEBAR_MENU_ITEMS.BACKUPS.relatedPowerups.some(powerup => bot.powerups.includes(powerup)) ? [{
                    key: `bots-${SIDEBAR_MENU_ITEMS.BACKUPS.key}`,
                    icon: <MenuIcon title={t('Backups')} icon={SIDEBAR_MENU_ITEMS.BACKUPS.icon} showTooltip={!isSidebarOpen} />,
                    label: t('Backups'),
                    onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'backups' }).toString()}`)
                  }] : [],
                  ...bot && bot.powerups && SIDEBAR_MENU_ITEMS.SETTINGS.relatedPowerups.some(powerup => bot.powerups.includes(powerup)) ? [{
                    key: `bots-${SIDEBAR_MENU_ITEMS.SETTINGS.key}`,
                    icon: <MenuIcon title={t('Settings')} icon={SIDEBAR_MENU_ITEMS.SETTINGS.icon} showTooltip={!isSidebarOpen} />,
                    label: t('Settings'),
                    onTitleClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'settings' }).toString()}`),
                    children: [
                      {
                        key: 'bots-settings-settings',
                        icon: <MenuIcon title={t('Configuration')} icon={<BoxIcon name="brush" />} hidden={isSidebarOpen} />,
                        label: t('Configuration'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'settings', tab: 'settings' }).toString()}`)
                      },
                      {
                        key: 'bots-settings-knowledge-base-sources',
                        icon: <MenuIcon title={t('Knowledge Bases')} icon={<BoxIcon name="book-open" />} hidden={isSidebarOpen} />,
                        label: t('Knowledge Bases'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'settings', tab: 'knowledge-base-sources' }).toString()}`)
                      },
                      {
                        key: 'bots-settings-privacy-settings',
                        icon: <MenuIcon title={t('Privacy')} icon={<BoxIcon name="lock" />} hidden={isSidebarOpen} />,
                        label: t('Privacy'),
                        onClick: () => navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?${createSearchParams({ view: 'settings', tab: 'privacy-settings' }).toString()}`)
                      }
                    ]
                  }] : []
                ] : []
              ]}
            />

            {
              tenantId && bot ? (
                <div
                  className={classNames('word-break-break-all fs-sm text-muted p-4', {
                    'd-none': !isSidebarOpen
                  })}
                  style={{ minHeight: 250 }}
                >
                  <div className="fw-bold">
                    {t('Created:')}
                  </div>

                  <div className="mb-1">
                    {moment(bot.created).tz(bot.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT) }
                  </div>

                  <div className="fw-bold">
                    {t('Last edit:')}
                  </div>

                  <div className="mb-1">
                    {moment(bot.lastChanged).tz(bot.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT)}
                  </div>

                  <div className="fw-bold">
                    {t('Editor:')}
                  </div>

                  <div>
                    {bot.lastChangedBy?.name || ''}
                  </div>

                  <div className="mb-1">
                    {bot.lastChangedBy?.email || ''}
                  </div>
                </div>
              ) : null
            }
          </Fragment>
        );

      case LABS.OBSERVATORY.key:
        var observatorySearchParams = location.pathname.includes('/observatory') ? {
          ...(searchParams.get('tenantsFilter') ? { tenantsFilter: searchParams.get('tenantsFilter') } : {}),
          ...(searchParams.get('startDate') ? { startDate: searchParams.get('startDate') } : {}),
          ...(searchParams.get('endDate') ? { endDate: searchParams.get('endDate') } : {})
        } : {};

        return (
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={selectedKeys}
            defaultOpenKeys={defaultOpenKeys}
            className={classNames(`${baseClassName}__lab-menu scrollbar-custom ${baseClassName}__lab-menu--observatory`, {
              'ant-menu-inline-collapsed': !isSidebarOpen
            })}
            items={[
              {
                key: 'observatory-sessions',
                icon: <MenuIcon title={t('Sessions')} icon={<BoxIcon name="list-ul" />} showTooltip={!isSidebarOpen} />,
                label: t('Sessions'),
                onTitleClick: () => navigate(`${replaceUrlParams(NAV_CHAT_SESSIONS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`),
                children: [
                  {
                    key: 'observatory-chat-sessions',
                    icon: <MenuIcon title={t('Chat Sessions')} icon={<BoxIcon name="conversation" />} hidden={isSidebarOpen} />,
                    label: <Link to={`${replaceUrlParams(NAV_CHAT_SESSIONS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`}>{t('Chat Sessions')}</Link>,
                    onClick: () => navigate(`${replaceUrlParams(NAV_CHAT_SESSIONS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`)
                  },
                  {
                    key: 'observatory-missed-questions',
                    icon: <MenuIcon title={t('Missed Questions')} icon={<BoxIcon name="question-mark" />} hidden={isSidebarOpen} />,
                    label: <Link to={`${replaceUrlParams(NAV_MISSED_QUESTIONS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`}>{t('Missed Questions')}</Link>,
                    onClick: () => navigate(`${replaceUrlParams(NAV_MISSED_QUESTIONS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`)
                  },
                  {
                    key: 'observatory-surveys',
                    icon: <MenuIcon title={t('Surveys')} icon={<BoxIcon name="clipboard" />} hidden={isSidebarOpen} />,
                    label: <Link to={`${replaceUrlParams(NAV_SURVEYS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`}>{t('Surveys')}</Link>,
                    onClick: () => navigate(`${replaceUrlParams(NAV_SURVEYS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`)
                  }
                ]
              },
              {
                key: 'observatory-analytics',
                icon: <MenuIcon title={t('Analytics')} icon={<BoxIcon name="bar-chart-alt-2" />} showTooltip={!isSidebarOpen} />,
                label: t('Analytics'),
                onTitleClick: () => navigate(`${replaceUrlParams(NAV_ANALYTICS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`),
                children: [
                  {
                    key: 'observatory-analytics-analytics',
                    icon: <MenuIcon title={t('Analytics')} icon={<BoxIcon name="line-chart" />} hidden={isSidebarOpen} />,
                    label: <Link to={`${replaceUrlParams(NAV_ANALYTICS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`}>{t('Analytics')}</Link>,
                    onClick: () => navigate(`${replaceUrlParams(NAV_ANALYTICS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`)
                  },
                  ...IS_SCHEDULED_REPORTS_ENABLED && userPermissions.includes(PERMISSIONS_ORG.ADMIN) ? [{
                    key: 'observatory-analytics-reports',
                    icon: <MenuIcon title={t('Reports')} icon={<BoxIcon name="file" />} hidden={isSidebarOpen} />,
                    label: <Link to={`${replaceUrlParams(NAV_SCHEDULED_REPORTS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`}>{t('Reports')}</Link>,
                    onClick: () => navigate(`${replaceUrlParams(NAV_SCHEDULED_REPORTS, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`)
                  }] : []
                ]
              },
              {
                key: 'observatory-analytics-monitoring',
                icon: <MenuIcon title={t('Monitoring')} icon={<BoxIcon name="server" />} showTooltip={!isSidebarOpen} />,
                label: <Link to={`${replaceUrlParams(NAV_MONITORING, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`}>{t('Monitoring')}</Link>,
                onClick: () => navigate(`${replaceUrlParams(NAV_MONITORING, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`)
              },
              ...(IS_AUTOMATED_AGENT_TESTING_ENABLED
                ? [{
                  key: 'observatory-analytics-testing',
                  icon: <MenuIcon title={t('Testing')} icon={<PlayCircleOutlined />} showTooltip={!isSidebarOpen} />,
                  label: <Link to={`${replaceUrlParams(NAV_ORGANIZATION_TESTING, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`}>{t('Testing')}</Link>,
                  onClick: () => navigate(`${replaceUrlParams(NAV_ORGANIZATION_TESTING, { orgId })}?${createSearchParams({ ...observatorySearchParams }).toString()}`)
                }]
                : []
              )
            ]}
          />
        );

      case LABS.LIVECHAT.key:
        return (
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={selectedKeys}
            defaultOpenKeys={defaultOpenKeys}
            className={classNames(`${baseClassName}__lab-menu scrollbar-custom ${baseClassName}__lab-menu--livechat`, {
              'ant-menu-inline-collapsed': !isSidebarOpen
            })}
            items={[
              ...userPermissions.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT) ? [{
                key: 'livechat-new',
                icon: <MenuIcon title={t('Inbox')} icon={<BoxIcon name="comment" />} showTooltip={!isSidebarOpen} />,
                label: <Link to={`${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.NEW }).toString()}`}>{t('Inbox')}</Link>,
                onClick: () => navigate(`${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.NEW }).toString()}`)
              }] : [],
              ...userPermissions.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT) ? [{
                key: 'livechat-requests-history',
                icon: <MenuIcon title={t('History')} icon={<BoxIcon name="history" />} showTooltip={!isSidebarOpen} />,
                label: <Link to={`${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY }).toString()}`}>{t('History')}</Link>,
                onClick: () => navigate(`${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY }).toString()}`)
              }] : [],
              ...userPermissions.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT) ? [{
                key: 'livechat-settings',
                icon: <MenuIcon title={t('My Settings')} icon={<BoxIcon name="slider" />} showTooltip={!isSidebarOpen} />,
                label: <Link to={`${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.SETTINGS }).toString()}`}>{t('My Settings')}</Link>,
                onClick: () => navigate(`${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.SETTINGS }).toString()}`)
              }] : [],
              ...userPermissions.includes(ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN) ? [{
                key: 'livechat-admin-settings',
                icon: <MenuIcon title={t('Admin Panel')} icon={<BoxIcon name="cog" />} showTooltip={!isSidebarOpen} />,
                label: <Link to={`${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS }).toString()}`}>{t('Admin Panel')}</Link>,
                onClick: () => navigate(`${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS }).toString()}`)
              }] : []
            ]}
          />
        );

      default:
        return null;
    }
  };

  const renderOrgMenu = () => {
    return (
      <Menu
        mode="inline"
        theme="dark"
        className={classNames(`${baseClassName}__org-menu scrollbar-custom scrollbar-custom-dark`, {
          'ant-menu-inline-collapsed': isOrgSiderCollapsed
        })}
        selectedKeys={selectedKeys}
        defaultOpenKeys={defaultOpenKeys}
        onClick={() => setCurrentLab(LABS.ORG.key)}
        items={[
          {
            key: 'org',
            icon: <MenuIcon title={t('Overview')} icon={<BoxIcon name="tachometer" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Overview'),
            onClick: () => navigate(replaceUrlParams(NAV_DASHBOARD, { orgId }))
          },
          {
            key: 'org-bots',
            icon: <MenuIcon title={t('Agents')} icon={<BoxIcon name="bot" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Agents'),
            onClick: () => navigate(replaceUrlParams(NAV_BOTS, { orgId }))

          },
          ...userPermissions.includes(PERMISSIONS_ORG.ADMIN) ? [{
            key: 'org-users',
            icon: <MenuIcon title={t('Users')} icon={<BoxIcon name="group" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Users'),
            popupClassName: `${baseClassName}__org-menu__popup`,
            popupOffset: [12, 0],
            onTitleClick: () => navigate(replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })),
            children: [
              {
                key: 'org-settings-users-users',
                icon: <MenuIcon title={t('Users')} icon={<BoxIcon name="user" />} hidden={!isOrgSiderCollapsed} isOrgIcon />,
                label: t('Users'),
                onClick: () => navigate(`${replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })}?${createSearchParams({ tab: 'users' }).toString()}`)
              },
              {
                key: 'org-settings-users-user-groups',
                icon: <MenuIcon title={t('User Groups')} icon={<BoxIcon name="group" />} hidden={!isOrgSiderCollapsed} isOrgIcon />,
                label: t('User Groups'),
                onClick: () => navigate(`${replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })}?${createSearchParams({ tab: 'user-groups' }).toString()}`)
              },
              {
                key: 'org-settings-users-user-invitations',
                icon: <MenuIcon title={t('User Invitations')} icon={<BoxIcon name="envelope" />} hidden={!isOrgSiderCollapsed} isOrgIcon />,
                label: t('User Invitations'),
                onClick: () => navigate(`${replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })}?${createSearchParams({ tab: 'user-invitations' }).toString()}`)
              }
            ]
          }] : [],
          {
            key: 'org-settings',
            icon: <MenuIcon title={t('Settings')} icon={<BoxIcon name="cog" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Settings'),
            popupClassName: `${baseClassName}__org-menu__popup`,
            popupOffset: [12, 0],
            onTitleClick: () => navigate(replaceUrlParams(NAV_ORGANIZATION_SETTINGS, { orgId })),
            children: [
              {
                key: 'org-settings-configuration',
                icon: <MenuIcon title={t('Configuration')} icon={<BoxIcon name="slider" />} hidden={!isOrgSiderCollapsed} isOrgIcon />,
                label: t('Configuration'),
                onClick: () => navigate(`${replaceUrlParams(NAV_ORGANIZATION_SETTINGS, { orgId })}?${createSearchParams({ tab: 'configuration' }).toString()}`)
              },
              {
                key: 'org-settings-login',
                icon: <MenuIcon title={t('Login Settings')} icon={<BoxIcon name="lock" />} hidden={!isOrgSiderCollapsed} isOrgIcon />,
                label: t('Login Settings'),
                onClick: () => navigate(`${replaceUrlParams(NAV_ORGANIZATION_SETTINGS, { orgId })}?${createSearchParams({ tab: 'login' }).toString()}`)
              }
            ]
          },
          {
            key: 'org-integrations',
            icon: <MenuIcon title={t('Integrations')} icon={<BoxIcon name="plug" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Integrations'),
            onClick: () => navigate(replaceUrlParams(NAV_ORGANIZATION_INTEGRATIONS, { orgId }))
          },
          ...(IS_MEDIA_MANAGER_ENABLED ? [{
            key: 'org-media',
            icon: <MenuIcon title={t('Media Manager')} icon={<BoxIcon name="images" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Media Manager'),
            onClick: () => navigate(replaceUrlParams(NAV_MEDIA, { orgId }))
          }] : []),
          {
            key: 'org-knowledge-bases',
            icon: <MenuIcon title={t('Knowledge Bases')} icon={<BoxIcon name="book-alt" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Knowledge Bases'),
            onClick: () => navigate(replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASES, { orgId }))
          },
          ...userPermissions.includes(PERMISSIONS_ORG.ADMIN) ? [{
            key: 'org-logs',
            icon: <MenuIcon title={t('Audit Logs')} icon={<BoxIcon name="list-ul" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Audit Logs'),
            onClick: () => navigate(replaceUrlParams(NAV_ORGANIZATION_LOGS, { orgId }))
          }] : [],
          ...(IS_CONTACTS_ENABLED ? [{
            key: 'org-contacts',
            icon: <MenuIcon title={t('Contacts')} icon={<BoxIcon name="desktop" />} showTooltip={isOrgSiderCollapsed} isOrgIcon />,
            label: t('Contacts'),
            onClick: () => navigate(replaceUrlParams(NAV_CONTACTS, { orgId }))
          }] : [])
        ]}
      />
    );
  };

  return (
    <Fragment>
      {
        isMobileOrTabletSize ? (
          <Drawer
            title={t('Menu')}
            open={isSidebarOpen}
            placement="right"
            onClose={() => toggleOpen(false)}
            forceRender
            className={`${baseClassName}__drawer`}
          >
            {renderLabMenu()}
          </Drawer>
        ) : (
          <span
            className={classNames(baseClassName, {
              [`${baseClassName}--hasLabSidebar`]: isLabSidebarVisible,
              [`${baseClassName}--hasOrg`]: userPermissions.some(permission => Object.values(PERMISSIONS_ORG).includes(permission)),
              [`${baseClassName}--hasBot`]: userPermissions.some(permission => Object.values(PERMISSIONS_TENANT).includes(permission)) || userPermissions.some(permission => Object.values(PERMISSIONS_ORG).includes(permission)),
              [`${baseClassName}--hasObservatory`]: userPermissions.some(permission => Object.values(PERMISSIONS_ORG).includes(permission)),
              [`${baseClassName}--hasLivechat`]: (IS_LIVECHAT_CONSOLE_ONLY || IS_LIVECHAT_CONVERSATIONS_ENABLED) && hasLiveChatAccess(currentUser, orgId),
              [`${baseClassName}--isFullScreenMode`]: isFullScreenMode
            })}
            style={{
              // If we are in Live-Chat console only mode or in MS Teams, we need to add some paddings, as there is no header and the sider is moved
              ...IS_LIVECHAT_CONSOLE_ONLY || is.inMicrosoftTeams() ? {
                paddingTop: paddingXs
              } : {}
            }}
          >
            {/* Organization sidebar */}
            {
              (IS_LIVECHAT_CONSOLE_ONLY || is.inMicrosoftTeams()) ? null : (
                <Sider
                  trigger={null}
                  collapsible
                  breakpoint="lg"
                  width={!isSidebarOpen && currentLab === LABS.ORG.key ? 72 : isSidebarOpen && currentLab === LABS.ORG.key ? 286 : 50}
                  className={classNames(`${baseClassName}__org`, {
                    'd-none': isFullScreenMode,
                    'ant-layout-sider-collapsed': isOrgSiderCollapsed
                  })}
                >
                  <div className={`${baseClassName}__org__mask`} />
                  {renderOrgMenu()}
                </Sider>
              )
            }

            {/* Lab sidebar */}
            {
              isLabSidebarVisible ? (
                <Sider
                  trigger={null}
                  collapsible
                  breakpoint="lg"
                  onCollapse={() => toggleOpen(!isSidebarOpen)}
                  width={isSidebarOpen ? 240 : 60}
                  theme="light"
                  className={classNames(`${baseClassName}__lab`, {
                    'd-none': isFullScreenMode,
                    'ant-layout-sider-collapsed': !isSidebarOpen
                  })}
                >
                  {renderLabMenu()}
                </Sider>
              ) : null
            }

            <LabSwitcher
              onArrowClick={() => toggleOpen(!isSidebarOpen)}
              currentLab={currentLab}
              setCurrentLab={setCurrentLab}
            />
          </span>
        )
      }
    </Fragment>
  );
};

MenuIcon.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  hidden: PropTypes.bool,
  showTooltip: PropTypes.bool,
  isOrgIcon: PropTypes.bool
};

export default GlobalSider;
